import type { ReactElement } from "react"
import styled from "styled-components"
import {
  DEFAULT_FILTERS,
  isFiltering,
  isSectionVisible,
  type LibraryFilters,
  matchesSearch,
  matchesStatus,
} from "~/features/library/filters"
import { sectionStyle } from "~/shared/theme/tokens"
import { Button, CardGrid, Section, Skeleton, StateMessage } from "~/shared/ui"
import { useAccessWindowIndex } from "../AccessWindowContext"
import { useEntitlements } from "../api/useEntitlements"
import { alreadyServedCount, bucketBySection, ENTITLEMENT_SECTIONS, OTHER_SECTION_ID } from "../sections"
import { EntitlementCard } from "./EntitlementCard"

const SKELETON_KEYS = ["a", "b"] as const
const SKELETON_HEIGHT = "9rem"

const Footer = styled.div`
  display: flex;
  justify-content: center;
`

const SkeletonCard = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const Note = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textSecondary};
`

interface EntitlementSectionsProps {
  readonly filters?: LibraryFilters
}

/**
 * The commerce-backed half of the page: the three sections MLC has and the graph
 * does not, plus `Other`. One query feeds all of them — the rows arrive in a
 * single list and are bucketed client-side, because that is how MLC composes its
 * own page and it keeps this to one round trip.
 */
export const EntitlementSections = ({ filters = DEFAULT_FILTERS }: EntitlementSectionsProps): ReactElement => {
  const { items, isLoading, isError, isTruncated, isFetchingNextPage, fetchNextPage } = useEntitlements()
  const buckets = bucketBySection(items)
  const served = alreadyServedCount(items)
  const windows = useAccessWindowIndex()

  return (
    <>
      {ENTITLEMENT_SECTIONS.filter(section => isSectionVisible(filters, section.id)).map(section => {
        const loaded = buckets.get(section.id) ?? []
        const rows = loaded.filter(
          item => matchesSearch(filters, item.title) && matchesStatus(filters, item.window.state),
        )
        const qa = `digital-library-${section.id}`

        return (
          <Section
            key={section.id}
            title={section.title}
            qaHook={`${qa}-section`}
            count={rows.length}
            /* Only the true fallback. It exists to prove nothing vanishes, which is
               a diagnostic job — worth having on the page and not worth the room. */
            collapsible={section.id === OTHER_SECTION_ID}
          >
            {isError ? (
              <StateMessage tone="error" qaHook={`${qa}-error`}>
                {`We couldn't load your ${section.title.toLowerCase()} right now.`}
              </StateMessage>
            ) : null}

            {isLoading ? (
              <CardGrid data-qa-hook={`${qa}-loading`}>
                {SKELETON_KEYS.map(key => (
                  <SkeletonCard key={key}>
                    <Skeleton $height={SKELETON_HEIGHT} />
                    <Skeleton />
                  </SkeletonCard>
                ))}
              </CardGrid>
            ) : null}

            {!isLoading && !isError && rows.length === 0 ? (
              <StateMessage qaHook={`${qa}-empty`}>
                {isFiltering(filters) && loaded.length > 0
                  ? `No matches in ${section.title.toLowerCase()}.`
                  : section.emptyMessage}
              </StateMessage>
            ) : null}

            {rows.length > 0 ? (
              <CardGrid data-qa-hook={`${qa}-grid`}>
                {rows.map(item => (
                  <EntitlementCard key={item.id} item={item} section={sectionStyle[section.id]} qaHook={`${qa}-card`} />
                ))}
              </CardGrid>
            ) : null}

            {section.id === OTHER_SECTION_ID && rows.length > 0 ? (
              <Note data-qa-hook={`${qa}-explainer`}>
                {`${rows.length} entitlement${rows.length === 1 ? "" : "s"} this build cannot classify. myLifeway renders these in no section at all, so they are invisible there — see FINDINGS.md Finding D.`}
              </Note>
            ) : null}

            {section.id === OTHER_SECTION_ID && served > 0 ? (
              <Note data-qa-hook={`${qa}-served-note`}>
                {`${served} further entitlement${served === 1 ? " is" : "s are"} not listed here because Studies, Digital Events and Ebooks above already show ${served === 1 ? "it" : "them"} — live from the DCE graph, or rebuilt from the seat when libraryV2 does not return it — greyed out once access has ended, which is the only way an expired item can appear at all, and as a live reader link for an ebook the graph never listed. Seat dates and Manage Access links are matched onto those cards by title, since libraryV2 serves no join key — see FINDINGS.md gaps 23, 25 and 30.`}
                {windows.ambiguousKeys.length > 0
                  ? ` ${windows.ambiguousKeys.length} title${windows.ambiguousKeys.length === 1 ? "" : "s"} matched more than one seat and ${windows.ambiguousKeys.length === 1 ? "was" : "were"} left unannotated rather than guessed.`
                  : ""}
              </Note>
            ) : null}

            {/*
              Not a pager: `useEntitlements` fetches every page on its own, because
              the bucketing above is only correct over the whole list. This is the
              escape hatch for the safety cap, which a healthy account never hits.
            */}
            {section.id === OTHER_SECTION_ID && isTruncated ? (
              <Footer>
                <Button
                  type="button"
                  $variant="outline"
                  data-qa-hook={`${qa}-load-more`}
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage ? "Loading…" : "Load more entitlements"}
                </Button>
              </Footer>
            ) : null}
          </Section>
        )
      })}
    </>
  )
}
